import { QueryClient, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { CustomerInvoice } from 'src/types/CustomerInvoice';
import { CustomerMonthlyBill } from 'src/types/CustomerMonthlyBill';
import { DeviceContract } from 'src/types/DeviceContract';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';
import { get, post, put, destroy } from '../utils';

export function createDeviceContract(contract: Partial<DeviceContract>): Promise<DeviceContract> {
  return post<DeviceContract>('devicecontract', contract);
}

export function getDeviceContract(deviceContractId: string): Promise<DeviceContract> {
  return get<DeviceContract>(`devicecontract/${deviceContractId}`);
}

export function generateBillingData(deviceContractId: string, timestamp: string): Promise<CustomerMonthlyBill> {
  return post<CustomerMonthlyBill>(`devicecontract/${deviceContractId}/generateBillingData`, { timestamp });
}

export function generateAllBillingData(timestamp: string): Promise<CustomerMonthlyBill[]> {
  return post<CustomerMonthlyBill[]>('devicecontract/generateAllBillingData', {
    timestamp,
  });
}

export function useQueryAllBillingData(timestamp: string): UseQueryResult<CustomerMonthlyBill[], Error> {
  return useQuery(['AllBillingData', timestamp], () => generateAllBillingData(timestamp));
}

export function generateInvoiceForMonth(deviceContractIds: string[], timestamp: string): Promise<CustomerInvoice> {
  return post<CustomerInvoice>(`devicecontract/generateInvoiceForMonth`, {
    deviceContractIds,
    timestamp,
  });
}

export function generateAllInvoicesForMonth(timestamp: string): Promise<CustomerInvoice[]> {
  return post<CustomerInvoice[]>('devicecontract/generateAllInvoicesForMonth', {
    timestamp,
  });
}

export function deleteDeviceContract(deviceContractId: string): Promise<void> {
  return destroy(`devicecontract/${deviceContractId}`);
}

export interface DeviceContractSearchParams {
  customerId?: string;
  createdFromCustomerOrderId?: string;
  includeDeleted?: boolean;
}

export function searchDeviceContracts(params: DeviceContractSearchParams): Promise<DeviceContract[]> {
  return get<DeviceContract[]>('devicecontract', { ...params });
}

export enum DeviceContractQueryKeys {
  DeviceContractPage = 'DeviceContractPage',
}

/** Sort fields the contract search accepts. Mirrors the API's DEVICE_CONTRACT_SORT_FIELDS. */
export const DEVICE_CONTRACT_SORT_FIELDS = [
  'customerName',
  'contractType',
  'subscriptionSku',
  'costPerDevice',
  'deviceCount',
  'startsAt',
  'expiresAt',
  'createdAt',
] as const;

/** How a row is ordered by each of {@link DEVICE_CONTRACT_SORT_FIELDS}. */
export const getDeviceContractSortValue = sortValueFrom<DeviceContract>({
  // `?? undefined` rather than `?? ''`: undefined means "this row has no value", which the
  // fallback comparator sorts the way MySQL sorts NULL — first ascending, last descending.
  // An empty string would sort as a value and put unnamed rows in the wrong half.
  customerName: (contract) => contract.customerName ?? undefined,
  contractType: (contract) => contract.contractType,
  subscriptionSku: (contract) => contract.subscriptionSku,
  costPerDevice: (contract) => contract.costPerDevice,
  deviceCount: (contract) => contract.deviceCount,
  startsAt: (contract) => contract.startsAt,
  expiresAt: (contract) => contract.expiresAt,
  createdAt: (contract) => contract.createdAt,
});

/**
 * ONE page of the device-contract search, with the total.
 *
 * The Device Contracts screen asked for EVERY contract on the platform and rendered all of
 * them — and, because a contract row names a `customerId` and nothing else, it ALSO asked
 * `customer?name=%&includeDeleted=true` for every customer on the platform (107,677 rows)
 * purely to resolve one name per row, then joined the two lists in the browser.
 *
 * The API now returns `customerName` on the row itself, on BOTH branches, so the second
 * request is gone rather than merely paged.
 */
export function getDeviceContractsPage(
  request: PageRequest,
  filters: DeviceContractSearchParams = {},
  queryClient?: QueryClient
): Promise<Page<DeviceContract>> {
  return getPage<DeviceContract>(
    'devicecontract',
    request,
    { ...filters },
    {
      // The legacy branch reads customerId, createdFromCustomerOrderId, subscriptionSku,
      // contractType and the date bounds — never `search` or `sort`. Without these, the
      // search box would answer with the first N contracts on the platform while
      // ORG_SCALE_PAGINATION is off: not an error and not an empty state, a confident wrong
      // answer. These match the API's SEARCH_COLUMNS plus the customer name, so what the
      // box finds does not change when the flag does.
      searchable: (contract) => [
        contract.id,
        contract.customerName ?? '',
        contract.contractType,
        contract.subscriptionSku,
        contract.description ?? '',
      ],
      sortValue: getDeviceContractSortValue,
    },
    queryClient,
    [DeviceContractQueryKeys.DeviceContractPage]
  );
}

/** One page of device contracts, as a query. */
export function useDeviceContractsPage(
  request: PageRequest,
  filters: DeviceContractSearchParams = {}
): UseQueryResult<Page<DeviceContract>, Error> {
  // Shares the FLAG-OFF whole-list download across pages, sorts and keystrokes — without it
  // every one of those is a new react-query key over the same platform-wide array, and each
  // one re-downloads it.
  const queryClient = useQueryClient();
  return useQuery<Page<DeviceContract>, Error>(
    [DeviceContractQueryKeys.DeviceContractPage, request, filters],
    () => getDeviceContractsPage(request, filters, queryClient),
    {
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it. `isLoading` is false throughout under this — read `isFetching` to know
      // a request is in flight.
      keepPreviousData: true,
    }
  );
}


export function updateDeviceContract(deviceContractId: string, updatedFields: Partial<DeviceContract>): Promise<DeviceContract> {
  if (updatedFields.id && updatedFields.id !== deviceContractId) {
    throw new Error('Contract IDs must match');
  }

  const body = {
    ...updatedFields,
    id: deviceContractId,
  };

  return put<DeviceContract>(`devicecontract/${deviceContractId}`, body);
}
